import { useEffect, useState } from "react";
import BrandQR from "../components/BrandQR";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Plus,
  LogOut,
  Calendar,
  MapPin,
  Users,
  ArrowRight,
  Landmark,
} from "lucide-react";

function capacityColor(pct) {
  if (pct >= 100) return "bg-red-600";
  if (pct >= 80) return "bg-orange-500";
  return "bg-black";
}

function EventCard({ ev }) {
  const pct = Math.min(100, Math.round((ev.registered / ev.capacity) * 100));
  return (
    <Link
      to={`/organizer/events/${ev.id}`}
      className="block bg-white border border-zinc-200 rounded-md p-6 transition-all hover:-translate-y-0.5 hover:shadow-sm"
      data-testid={`event-card-${ev.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="overline mb-2">Event</p>
          <h3 className="font-heading text-xl font-semibold tracking-tight leading-tight">
            {ev.name}
          </h3>
        </div>
        <ArrowRight className="w-5 h-5 text-zinc-400" />
      </div>

      <div className="space-y-1.5 text-sm text-zinc-600 mb-5">
        <div className="flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5" />
          {new Date(ev.date).toLocaleString([], {
            dateStyle: "medium",
            timeStyle: "short",
          })}
        </div>
        {ev.location && (
          <div className="flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" />
            {ev.location}
          </div>
        )}
        <div className="flex items-center gap-2">
          <Users className="w-3.5 h-3.5" />
          {ev.registered} / {ev.capacity} registered
        </div>
      </div>

      <div className="w-full bg-zinc-100 h-2 rounded-sm overflow-hidden">
        <div
          className={`h-full ${capacityColor(pct)} transition-all`}
          style={{ width: `${pct}%` }}
          data-testid={`capacity-bar-${ev.id}`}
        />
      </div>

      <div className="mt-4 pt-4 border-t border-zinc-100 grid grid-cols-3 gap-2 text-center">
        <div>
          <p className="font-heading text-lg font-semibold tracking-tight">
            {ev.registered}
          </p>
          <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mt-0.5">
            Registered
          </p>
        </div>
        <div>
          <p
            className="font-heading text-lg font-semibold tracking-tight text-green-700"
            data-testid={`checked-in-${ev.id}`}
          >
            {ev.checked_in ?? 0}
          </p>
          <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mt-0.5">
            Checked-in
          </p>
        </div>
        <div>
          <p className="font-heading text-lg font-semibold tracking-tight">
            {ev.remaining ?? Math.max(0, ev.capacity - ev.registered)}
          </p>
          <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mt-0.5">
            Remaining
          </p>
        </div>
      </div>

      <div className="mt-3 flex justify-end text-xs">
        <span className="text-zinc-900 font-medium">₹{ev.price}</span>
      </div>
    </Link>
  );
}

export default function OrganizerDashboard() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payout, setPayout] = useState(null);
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/events/my");
      setEvents(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api
      .get("/organizer/payout")
      .then(({ data }) => setPayout(data))
      .catch(() => {});
  }, []);

  const totalRegs = events.reduce((a, e) => a + e.registered, 0);
  const totalCheckedIn = events.reduce((a, e) => a + (e.checked_in || 0), 0);
  const totalCap = events.reduce((a, e) => a + e.capacity, 0);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <nav className="bg-white border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/organizer/dashboard" className="flex items-center gap-2">
            <BrandQR />
            <span className="font-heading font-semibold tracking-tight">
              Growithevent
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <span
              className="hidden sm:block text-sm text-zinc-500"
              data-testid="organizer-email"
            >
              {user?.email}
            </span>
            <Link to="/organizer/payouts">
              <Button
                variant="outline"
                className="rounded-md border-zinc-200"
                data-testid="payout-settings-button"
              >
                <Landmark className="w-4 h-4 mr-1.5" />
                Payouts
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={() => {
                logout();
                nav("/");
              }}
              className="rounded-md border-zinc-200"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-1.5" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {payout && payout.available && !payout.configured && (
          <div
            className="mb-8 border border-amber-200 bg-amber-50 rounded-md p-4 flex items-center justify-between flex-wrap gap-3"
            data-testid="payout-banner"
          >
            <div className="flex items-center gap-3">
              <Landmark className="w-5 h-5 text-amber-600" />
              <p className="text-sm text-amber-800">
                Route transfers are disabled for now. Host settlements are processed manually after the event and refund window.
              </p>
            </div>
            <Link to="/organizer/payouts">
              <Button className="h-9 bg-black text-white hover:bg-zinc-800 rounded-md">
                Add bank account
              </Button>
            </Link>
          </div>
        )}
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="overline mb-3">Dashboard</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tighter">
              Your events
            </h1>
          </div>
          <Link to="/organizer/events/new">
            <Button
              className="h-11 bg-black text-white hover:bg-zinc-800 rounded-md px-5"
              data-testid="create-event-button"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Create event
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 rounded-md mb-10 overflow-hidden">
          {[
            { label: "Events", value: events.length },
            { label: "Paid registrations", value: totalRegs },
            { label: "Checked-in", value: totalCheckedIn },
            { label: "Total capacity", value: totalCap },
          ].map((s) => (
            <div key={s.label} className="bg-white p-6">
              <p className="overline mb-2">{s.label}</p>
              <p
                className="font-heading text-3xl font-semibold tracking-tight"
                data-testid={`stat-${s.label.toLowerCase().replace(/ /g, "-")}`}
              >
                {s.value}
              </p>
            </div>
          ))}
        </div>

        {loading ? (
          <p className="text-zinc-500">Loading events…</p>
        ) : events.length === 0 ? (
          <div
            className="border border-dashed border-zinc-300 rounded-md p-12 text-center bg-white"
            data-testid="empty-state"
          >
            <Calendar className="w-10 h-10 text-zinc-300 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-heading text-2xl font-semibold tracking-tight mb-2">
              No events yet
            </h3>
            <p className="text-zinc-500 mb-6">
              Create your first event and share the registration link.
            </p>
            <Link to="/organizer/events/new">
              <Button
                className="h-11 bg-black text-white hover:bg-zinc-800 rounded-md px-5"
                data-testid="empty-create-event-button"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Create event
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {events.map((ev) => (
              <EventCard key={ev.id} ev={ev} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
